/**
 * @module DialogRoot
 *
 * @see https://0.vuetifyjs.com/components/disclosure/dialog
 *
 * @remarks
 * Root component for dialog contexts. Creates and provides dialog context
 * to child Dialog components. Manages open/closed state via v-model binding.
 */

<script lang="ts">
  // Composables
  import { createContext } from '#v0/composables/createContext'

  // Types
  import type { AtomProps } from '#v0/components/Atom'
  import type { ShallowRef } from 'vue'

  export interface DialogContext {
    isOpen: ShallowRef<boolean>
    id: string
    titleId: string
    descriptionId: string
    /** Whether a Dialog.Title child is currently mounted */
    hasTitle: ShallowRef<boolean>
    /** Whether a Dialog.Description child is currently mounted */
    hasDescription: ShallowRef<boolean>
    open: () => void
    close: () => void
  }

  export interface DialogRootProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
    /** Unique identifier for the dialog (auto-generated if not provided) */
    id?: string
  }

  export interface DialogRootSlotProps {
    /** Unique identifier */
    id: string
    /** Whether the dialog is currently open */
    isOpen: boolean
    /** Open the dialog */
    open: () => void
    /** Close the dialog */
    close: () => void
  }

  export const [useDialogContext, provideDialogContext] = /* @__PURE__ */ createContext<DialogContext>()
</script>

<script setup lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Utilities
  import { useId } from '#v0/utilities'
  import { shallowRef, toRef } from 'vue'

  defineOptions({ name: 'DialogRoot' })

  defineSlots<{
    default: (props: DialogRootSlotProps) => any
  }>()

  defineEmits<{
    'update:model-value': [value: boolean]
  }>()

  const {
    as = null,
    namespace = 'v0:dialog',
    id: _id,
  } = defineProps<DialogRootProps>()

  const isOpen = defineModel<boolean>({ default: false })

  const id = _id ?? useId()
  const titleId = `${id}-title`
  const descriptionId = `${id}-description`
  const hasTitle = shallowRef(false)
  const hasDescription = shallowRef(false)

  function open () {
    isOpen.value = true
  }

  function close () {
    isOpen.value = false
  }

  provideDialogContext(namespace, {
    isOpen,
    id,
    titleId,
    descriptionId,
    hasTitle,
    hasDescription,
    open,
    close,
  })

  const slotProps = toRef((): DialogRootSlotProps => ({
    id,
    isOpen: isOpen.value,
    open,
    close,
  }))
</script>

<template>
  <Atom
    :as
    renderless
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
